import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LayersIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

export default function Templates() {
  const [templates, setTemplates] = useState([]);

  const load = async () => {
    const { data } = await api.get("/templates");
    setTemplates(data);
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      setTemplates((t) => t.filter((x) => x.id !== id));
      toast.success("Template deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1200px]">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">STUDIO · PRESETS</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Video <span className="font-bold text-amber-400">templates.</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          Save your favourite settings (style, aspect, music, voice) as templates and apply them
          to new projects in one click. Create templates from the last step of the project workflow.
        </p>
      </div>

      {templates.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-lg p-12 text-center">
          <LayersIcon className="w-10 h-10 text-amber-400 mx-auto mb-4" strokeWidth={1.5} />
          <div className="font-heading text-xl font-bold tracking-tight">No templates yet</div>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            Create your first video and click &quot;Save as template&quot; in the last step to store your favourite look.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          <AnimatePresence>
            {templates.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-[#121214] border border-white/5 rounded-lg p-5 hover:border-white/15 transition-colors"
                data-testid={`template-${t.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-heading text-lg font-bold tracking-tight">{t.name}</div>
                    <div className="text-[10px] font-mono-tech text-zinc-500 mt-1 uppercase tracking-wider">
                      {t.settings.style} · {t.settings.aspect} · {t.settings.resolution}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => del(t.id)}
                    data-testid={`delete-template-${t.id}`}
                    className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 h-8 px-2"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
                <div className="space-y-2 text-xs text-zinc-400">
                  <Row label="Music" value={t.settings.music_track ? t.settings.music_track.replace("_", " ").replace(".mp3", "") : "None"} />
                  <Row label="Voice-over" value={t.settings.voice_enabled ? t.settings.language.toUpperCase() : "Off"} />
                  <Row label="Captions" value={t.settings.captions_enabled ? "On" : "Off"} />
                  <Row label="Branding" value={t.settings.branding_enabled ? "On" : "Off"} />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-1.5 last:border-0">
      <span className="font-mono-tech text-[9px] text-zinc-500">{label.toUpperCase()}</span>
      <span className="text-white capitalize">{String(value)}</span>
    </div>
  );
}
